import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Delete, LogIn, Loader2, Maximize } from 'lucide-react';

const LoginPage = () => {
  const [step, setStep] = useState('code'); // 'code' | 'passcode'
  const [userCode, setUserCode] = useState('');
  const [passcode, setPasscode] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const enterFullscreen = () => {
    const el = document.documentElement;
    const rfs = el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen;
    if (rfs) {
      rfs.call(el).catch(() => {});
    }
  };

  const currentValue = step === 'code' ? userCode : passcode;
  const setCurrentValue = step === 'code' ? setUserCode : setPasscode;

  const handleDigit = (digit) => {
    if (currentValue.length >= 8) return;
    setCurrentValue(prev => prev + digit);
  };

  const handleBackspace = () => {
    setCurrentValue(prev => prev.slice(0, -1));
  };

  const handleClear = () => {
    setCurrentValue('');
  };

  // Keyboard support
  const handleKeyDown = useCallback((e) => {
    if (loading) return;
    if (e.key >= '0' && e.key <= '9') {
      e.preventDefault();
      if (step === 'code') setUserCode(prev => prev.length < 8 ? prev + e.key : prev);
      else setPasscode(prev => prev.length < 8 ? prev + e.key : prev);
    } else if (e.key === 'Backspace') {
      e.preventDefault();
      if (step === 'code') setUserCode(prev => prev.slice(0, -1));
      else setPasscode(prev => prev.slice(0, -1));
    } else if (e.key === 'Escape') {
      e.preventDefault();
      if (step === 'code') setUserCode('');
      else setPasscode('');
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // Handled via form/button click below
    }
  }, [step, loading]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handleNext = () => {
    if (step === 'code') {
      if (!userCode) {
        toast.error('Enter your user code');
        return;
      }
      setStep('passcode');
    }
  };

  const handleBack = () => {
    if (step === 'passcode') {
      setPasscode('');
      setStep('code');
    }
  };

  const handleSubmit = async () => {
    if (!userCode || !passcode) {
      toast.error('Enter user code and passcode');
      return;
    }

    setLoading(true);
    try {
      const user = await login(userCode, passcode);
      enterFullscreen();
      toast.success(`Welcome, ${user.username}`);
      navigate(user.role === 'operator' ? '/till' : '/admin');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
      setPasscode('');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyAction = () => {
    if (step === 'code') handleNext();
    else handleSubmit();
  };

  // Keyboard Enter handler
  useEffect(() => {
    const handleEnter = (e) => {
      if (e.key === 'Enter' && !loading) {
        e.preventDefault();
        if (step === 'code' && userCode) handleNext();
        else if (step === 'passcode' && passcode) handleSubmit();
      }
    };
    window.addEventListener('keydown', handleEnter);
    return () => window.removeEventListener('keydown', handleEnter);
  });

  const digits = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

  return (
    <div className="login-bg min-h-screen flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" />

      <div className="relative z-10 w-full max-w-sm">
        <div className="backdrop-blur-xl bg-white/90 rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-slate-900 px-6 py-5 text-center">
            <h1 className="font-heading text-2xl font-bold text-white tracking-tight">Kasseport</h1>
            <p className="text-slate-400 text-sm mt-1">Point of Sale System</p>
          </div>

          <div className="p-6">
            {/* Step indicator */}
            <div className="flex items-center justify-center gap-2 mb-5">
              <div className={`w-2 h-2 rounded-full ${step === 'code' ? 'bg-slate-900' : 'bg-slate-300'}`} />
              <div className={`w-2 h-2 rounded-full ${step === 'passcode' ? 'bg-slate-900' : 'bg-slate-300'}`} />
            </div>

            {/* Label */}
            <p className="text-center text-sm font-medium text-slate-600 mb-3" data-testid="login-step-label">
              {step === 'code' ? 'Enter User Code' : 'Enter Passcode'}
            </p>

            {/* Display */}
            <div
              className="bg-slate-50 border border-slate-200 rounded-xl h-16 flex items-center justify-center mb-5 px-4"
              data-testid="login-display"
            >
              <span className="text-3xl font-mono font-bold tracking-[0.3em] text-slate-900">
                {step === 'code'
                  ? userCode || <span className="text-slate-300">----</span>
                  : passcode ? '\u2022'.repeat(passcode.length) : <span className="text-slate-300">----</span>
                }
              </span>
            </div>

            {/* Back button for passcode step */}
            {step === 'passcode' && (
              <button
                onClick={handleBack}
                className="text-sm text-slate-500 hover:text-slate-700 mb-3 flex items-center gap-1"
                data-testid="login-back-btn"
              >
                &larr; Change user code ({userCode})
              </button>
            )}

            {/* Numpad */}
            <div className="grid grid-cols-3 gap-2 mb-4">
              {digits.map((d) => (
                <button
                  key={d}
                  data-testid={`numpad-${d}`}
                  onClick={() => handleDigit(d)}
                  className="h-14 rounded-xl bg-white border border-slate-200 text-xl font-semibold text-slate-800 hover:bg-slate-50 active:bg-slate-100 active:scale-95 transition-all touch-press shadow-sm"
                >
                  {d}
                </button>
              ))}
              <button
                data-testid="numpad-clear"
                onClick={handleClear}
                className="h-14 rounded-xl bg-slate-100 border border-slate-200 text-sm font-medium text-slate-500 hover:bg-slate-200 active:scale-95 transition-all"
              >
                Clear
              </button>
              <button
                data-testid="numpad-0"
                onClick={() => handleDigit('0')}
                className="h-14 rounded-xl bg-white border border-slate-200 text-xl font-semibold text-slate-800 hover:bg-slate-50 active:bg-slate-100 active:scale-95 transition-all shadow-sm"
              >
                0
              </button>
              <button
                data-testid="numpad-backspace"
                onClick={handleBackspace}
                className="h-14 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-200 active:scale-95 transition-all"
              >
                <Delete className="w-5 h-5" />
              </button>
            </div>

            {/* Action button */}
            <button
              data-testid="login-submit"
              onClick={handleKeyAction}
              disabled={loading || !currentValue}
              className="w-full h-14 rounded-xl bg-slate-900 text-white font-semibold text-lg flex items-center justify-center gap-2 hover:bg-slate-800 active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : step === 'code' ? (
                <>Next</>
              ) : (
                <>
                  <LogIn className="w-5 h-5" />
                  Sign In
                </>
              )}
            </button>

            {/* Install as app */}
            <button
              data-testid="fullscreen-btn"
              onClick={enterFullscreen}
              className="w-full mt-2 h-10 rounded-xl border border-slate-200 text-slate-500 text-sm font-medium flex items-center justify-center gap-2 hover:bg-slate-50 active:scale-[0.98] transition-all"
            >
              <Maximize className="w-4 h-4" />
              Fullscreen Mode
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
